import React, {
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent
} from "react";

import type { LandingPageContent } from "../content/types";

const brandLogoSrc = "/assets/brand/production/v1/svg/erg-dbk-logo-horizontal-on-light.svg";

type BrandHeaderProps = {
  content: LandingPageContent;
  alternatePath: string;
};

export function BrandHeader({ content, alternatePath }: BrandHeaderProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);
  const lastMenuPointerTypeRef = useRef<string | null>(null);
  const mobileNavigationTriggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRefs = useRef(new Map<string, HTMLDivElement>());
  const primaryItems = content.navigation.items.filter((item) => item.kind === "link" || item.kind === "menu");
  const primaryAction = content.navigation.items.find((item) => item.kind === "cta");
  const secondaryActions = content.navigation.items.filter((item) => item.kind === "secondary");
  const mobileNavigationPanelId = `brand-header-${content.locale}-mobile-navigation`;

  useEffect(() => {
    if (!openMenuId) return;
    const activeMenuId = openMenuId;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      const activeMenu = menuRefs.current.get(activeMenuId);
      if (target instanceof Node && activeMenu?.contains(target)) return;
      setOpenMenuId(null);
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        setOpenMenuId(null);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (!mobileNavigationOpen) return;

    function closeMobileNavigation(restoreFocus: boolean) {
      setMobileNavigationOpen(false);
      setOpenMenuId(null);
      if (!restoreFocus) return;
      window.requestAnimationFrame(() => {
        mobileNavigationTriggerRef.current?.focus();
      });
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && headerRef.current?.contains(target)) return;
      closeMobileNavigation(false);
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeMobileNavigation(true);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileNavigationOpen]);

  function setMenuRef(id: string) {
    return (element: HTMLDivElement | null) => {
      if (element) {
        menuRefs.current.set(id, element);
      } else {
        menuRefs.current.delete(id);
      }
    };
  }

  function closeMenuOnBlur(id: string, event: FocusEvent<HTMLDivElement>) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) return;
    if (openMenuId === id) {
      setOpenMenuId(null);
    }
  }

  function openMenuOnFocus(id: string, event: FocusEvent<HTMLDivElement>) {
    if (lastMenuPointerTypeRef.current) return;
    if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 1100px)")?.matches === true) {
      return;
    }
    const target = event.target;
    if (target instanceof HTMLElement && target.matches(":focus-visible")) {
      setOpenMenuId(id);
    }
  }

  function closeMenuOnEscape(id: string, event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Escape" || openMenuId !== id) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.querySelector<HTMLButtonElement>(".brand-header__menu-trigger")?.focus();
    setOpenMenuId(null);
  }

  function openMenuOnPointerEnter(id: string, event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType === "touch") return;
    setOpenMenuId(id);
  }

  function handleMenuTriggerClick(id: string, event: MouseEvent<HTMLButtonElement>) {
    const supportsHover =
      typeof window !== "undefined" && window.matchMedia?.("(hover: hover)")?.matches === true;
    const usesCompactNavigation =
      typeof window !== "undefined" && window.matchMedia?.("(max-width: 1100px)")?.matches === true;
    const pointerType = lastMenuPointerTypeRef.current;
    lastMenuPointerTypeRef.current = null;
    const pointerRequiresToggle = pointerType === "touch" || pointerType === "pen";

    if (usesCompactNavigation || pointerRequiresToggle || !supportsHover) {
      const wasOpen = event.currentTarget.getAttribute("aria-expanded") === "true";
      setOpenMenuId(wasOpen ? null : id);
      return;
    }

    if (
      (event.detail === 0 && !pointerRequiresToggle)
      || (supportsHover && !usesCompactNavigation && !pointerRequiresToggle)
    ) {
      setOpenMenuId(id);
      return;
    }

    setOpenMenuId((currentMenuId) => (currentMenuId === id ? null : id));
  }

  function openMenuFromTrigger(id: string, event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "ArrowDown") return;
    event.preventDefault();
    setOpenMenuId(id);
    window.requestAnimationFrame(() => {
      menuRefs.current.get(id)?.querySelector<HTMLAnchorElement>(".brand-header__dropdown-link")?.focus();
    });
  }

  function toggleMobileNavigation() {
    setMobileNavigationOpen((current) => {
      const nextOpen = !current;
      if (!nextOpen) {
        setOpenMenuId(null);
      }
      return nextOpen;
    });
  }

  function closeMobileNavigationFromSelection() {
    setMobileNavigationOpen(false);
    setOpenMenuId(null);
  }

  return (
    <header
      className="brand-header"
      data-mobile-navigation-open={mobileNavigationOpen ? "true" : "false"}
      ref={headerRef}
    >
      <div className="brand-header__inner">
        <a className="brand-header__brand" href={content.routePath} aria-label={content.brand.homeAriaLabel}>
          <img
            className="brand-header__logo"
            src={brandLogoSrc}
            width="180"
            height="60"
            alt=""
            decoding="async"
            loading="eager"
          />
        </a>

        <button
          type="button"
          className="brand-header__mobile-toggle"
          aria-label={
            mobileNavigationOpen
              ? content.navigation.mobileMenuCloseLabel
              : content.navigation.mobileMenuOpenLabel
          }
          aria-expanded={mobileNavigationOpen}
          aria-controls={mobileNavigationPanelId}
          onClick={toggleMobileNavigation}
          ref={mobileNavigationTriggerRef}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>

        <div
          className="brand-header__navigation-strip"
          id={mobileNavigationPanelId}
          aria-label={content.navigation.mobileMenuPanelLabel}
        >
          <nav className="brand-header__nav" aria-label="Primary navigation">
            {primaryItems.map((item) => {
              if (item.kind === "menu") {
                const isOpen = openMenuId === item.id;
                const panelId = `brand-header-${content.locale}-${item.id}-menu`;

                return (
                  <div
                    className={`brand-header__menu${isOpen ? " is-open" : ""}`}
                    key={item.id}
                    ref={setMenuRef(item.id)}
                    onPointerEnter={(event) => openMenuOnPointerEnter(item.id, event)}
                    onPointerLeave={() => setOpenMenuId(null)}
                    onFocus={(event) => openMenuOnFocus(item.id, event)}
                    onBlur={(event) => closeMenuOnBlur(item.id, event)}
                    onKeyDown={(event) => closeMenuOnEscape(item.id, event)}
                  >
                    <button
                      type="button"
                      className="brand-header__nav-link brand-header__menu-trigger"
                      aria-haspopup="menu"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onPointerDown={(event) => {
                        lastMenuPointerTypeRef.current = event.pointerType;
                      }}
                      onTouchStart={() => {
                        lastMenuPointerTypeRef.current = "touch";
                      }}
                      onClick={(event) => handleMenuTriggerClick(item.id, event)}
                      onKeyDown={(event) => openMenuFromTrigger(item.id, event)}
                    >
                      <span>{item.label}</span>
                      <span className="brand-header__menu-chevron" aria-hidden="true" />
                    </button>
                    <div
                      className="brand-header__dropdown"
                      id={panelId}
                      role="menu"
                      aria-label={item.label}
                      hidden={!isOpen}
                    >
                      {item.items.map((menuItem) => (
                        <a
                          className="brand-header__dropdown-link"
                          href={menuItem.href}
                          key={menuItem.id}
                          role="menuitem"
                          onClick={closeMobileNavigationFromSelection}
                        >
                          {menuItem.label}
                        </a>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <a
                  className="brand-header__nav-link"
                  key={item.id}
                  href={item.href}
                  onClick={closeMobileNavigationFromSelection}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          <div className="brand-header__actions" aria-label="Account and language actions">
            {primaryAction ? (
              <a
                className="brand-header__action brand-header__action--primary"
                href={primaryAction.href}
                onClick={closeMobileNavigationFromSelection}
              >
                {primaryAction.label}
              </a>
            ) : null}
            {secondaryActions.map((item) => (
              <a
                className="brand-header__action brand-header__action--secondary"
                href={item.href}
                key={item.id}
                onClick={closeMobileNavigationFromSelection}
              >
                {item.label}
              </a>
            ))}
            <a
              className="brand-header__action brand-header__action--locale"
              href={alternatePath}
              onClick={closeMobileNavigationFromSelection}
            >
              {content.navigation.localeSwitchLabel}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
